import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { UserPlus, Copy, PaperPlaneRight, Trash, CheckCircle, Clock, X as XIcon, Envelope, WhatsappLogo } from "@phosphor-icons/react";

const STATUS_META = {
  pending:  { label: "Pending",  color: "text-amber-700 bg-amber-50 border-amber-300" },
  accepted: { label: "Accepted", color: "text-emerald-700 bg-emerald-50 border-emerald-300" },
  expired:  { label: "Expired",  color: "text-neutral-600 bg-neutral-100 border-neutral-300" },
  revoked:  { label: "Revoked",  color: "text-red-700 bg-red-50 border-red-300" },
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); }
  catch { return iso; }
}

export default function AdminTeam() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "designer" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invites");
      setInvites(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invites");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/invites", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        role: form.role,
      });
      setInvites((prev) => [data, ...prev.filter((i) => i.email !== data.email)]);
      const channels = [data.email_sent && "email", data.whatsapp_sent && "WhatsApp"].filter(Boolean).join(" + ");
      toast.success(channels ? `Invite sent via ${channels}` : "Invite created (delivery failed — copy the link)");
      setForm({ name: "", email: "", phone: "", role: "designer" });
      setShowForm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create invite");
    } finally { setCreating(false); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/admin/invites/${id}/resend`);
      setInvites((prev) => prev.map((i) => i.id === id ? data : i));
      const channels = [data.email_sent && "email", data.whatsapp_sent && "WhatsApp"].filter(Boolean).join(" + ");
      toast.success(channels ? `Resent via ${channels}` : "Delivery failed — copy the link");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invitation? The link will no longer work.")) return;
    try {
      await api.delete(`/admin/invites/${id}`);
      setInvites((prev) => prev.map((i) => i.id === id ? { ...i, status: "revoked" } : i));
      toast.success("Invite revoked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="pf-label mb-2">Admin · Team & Invitations</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Invite your team</h1>
            <p className="text-sm text-[var(--pf-text-2)] mt-2 max-w-xl">
              Send an email + WhatsApp invitation to designers, print vendors, and agency admins.
              They activate their account by setting a password — no public signup needed.
            </p>
          </div>
          <button onClick={() => setShowForm((v) => !v)} className="pf-btn-primary w-full sm:w-auto" data-testid="new-invite-btn">
            {showForm ? (<><XIcon size={16}/> Cancel</>) : (<><UserPlus size={16} weight="fill"/> New invitation</>)}
          </button>
        </div>

        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 mb-6 text-sm text-amber-900" data-testid="resend-sandbox-notice">
          <strong>Resend sandbox mode:</strong> Until you verify a sending domain at{" "}
          <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a>,
          invitation emails will only be delivered to your Resend account owner's email address. Other recipients need to be reached via WhatsApp
          or the <em>Copy link</em> button — the link works for anyone.
        </div>

        {showForm && (
          <form onSubmit={submit} className="border-2 border-[var(--pf-text)] bg-white rounded-2xl p-4 sm:p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="invite-form">
            <div>
              <label className="pf-label block mb-2">Full name</label>
              <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="pf-input" data-testid="invite-name" placeholder="Priya Sharma" />
            </div>
            <div>
              <label className="pf-label block mb-2">Email</label>
              <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="pf-input" data-testid="invite-email-input" placeholder="priya@studio.com" />
            </div>
            <div>
              <label className="pf-label block mb-2">Phone (optional, for WhatsApp)</label>
              <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="pf-input" data-testid="invite-phone" placeholder="+91 98765 43210" />
            </div>
            <div>
              <label className="pf-label block mb-2">Role</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["designer", "Designer"],
                  ["vendor", "Vendor"],
                  ["admin", "Admin"],
                ].map(([r, label]) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setForm({...form, role: r})}
                    className={`border-2 py-2 px-2 text-xs font-bold uppercase tracking-widest transition-colors ${form.role === r ? "bg-[var(--pf-text)] text-white border-[var(--pf-text)]" : "border-[var(--pf-border)] hover:border-[var(--pf-text)]"}`}
                    data-testid={`invite-role-${r}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <p className="text-xs text-[var(--pf-text-3)] flex-1">
                Invitation link expires in 7 days. Sent via email + WhatsApp (if phone provided).
              </p>
              <button type="submit" disabled={creating} className="pf-btn-primary" data-testid="send-invite">
                {creating ? "Sending…" : (<><PaperPlaneRight size={16} weight="fill"/> Send invitation</>)}
              </button>
            </div>
          </form>
        )}

        <div className="border border-[var(--pf-border)] bg-white rounded-2xl overflow-hidden" data-testid="invite-list">
          <div className="grid grid-cols-12 gap-2 px-4 sm:px-6 py-3 border-b border-[var(--pf-border)] pf-label bg-[var(--pf-surface-2)]">
            <div className="col-span-4 sm:col-span-3">Name</div>
            <div className="col-span-8 sm:col-span-4 truncate">Contact</div>
            <div className="hidden sm:block sm:col-span-2">Role</div>
            <div className="hidden sm:block sm:col-span-1">Status</div>
            <div className="hidden sm:block sm:col-span-2 text-right">Actions</div>
          </div>
          {loading && <div className="p-8 text-center pf-label">Loading…</div>}
          {!loading && invites.length === 0 && (
            <div className="p-10 text-center">
              <UserPlus size={40} weight="duotone" className="mx-auto text-[var(--pf-text-3)] mb-3"/>
              <div className="pf-label mb-1">No invitations yet</div>
              <div className="text-sm text-[var(--pf-text-2)]">Send your first team invite above.</div>
            </div>
          )}
          {!loading && invites.map((inv) => {
            const meta = STATUS_META[inv.status] || STATUS_META.pending;
            const isPending = inv.status === "pending";
            return (
              <div key={inv.id} className="grid grid-cols-12 gap-2 px-4 sm:px-6 py-4 border-b border-[var(--pf-border)] items-center" data-testid={`invite-row-${inv.id}`}>
                <div className="col-span-12 sm:col-span-3">
                  <div className="font-bold text-sm break-words">{inv.name}</div>
                  <div className="sm:hidden text-xs text-[var(--pf-text-3)] mt-0.5">{inv.role_label}</div>
                </div>
                <div className="col-span-12 sm:col-span-4 min-w-0">
                  <div className="text-xs text-[var(--pf-text-2)] truncate flex items-center gap-1">
                    <Envelope size={12}/> {inv.email}
                    {inv.email_sent && <CheckCircle size={12} weight="fill" className="text-emerald-500"/>}
                  </div>
                  {inv.phone && (
                    <div className="text-xs text-[var(--pf-text-2)] truncate flex items-center gap-1 mt-0.5">
                      <WhatsappLogo size={12}/> {inv.phone}
                      {inv.whatsapp_sent && <CheckCircle size={12} weight="fill" className="text-emerald-500"/>}
                    </div>
                  )}
                </div>
                <div className="hidden sm:block sm:col-span-2 text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)]">{inv.role_label}</div>
                <div className="col-span-6 sm:col-span-1">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 border rounded ${meta.color}`}>
                    {inv.status === "accepted" && <CheckCircle size={10} weight="fill"/>}
                    {inv.status === "pending" && <Clock size={10} weight="fill"/>}
                    {meta.label}
                  </span>
                </div>
                <div className="col-span-6 sm:col-span-2 flex items-center justify-end gap-1 flex-wrap">
                  <button onClick={() => copyLink(inv.invite_link)} title="Copy invite link" className="p-2 hover:bg-[var(--pf-surface-2)] rounded" data-testid={`copy-${inv.id}`}>
                    <Copy size={14}/>
                  </button>
                  {isPending && (
                    <>
                      <button onClick={() => resend(inv.id)} title="Resend" className="p-2 hover:bg-[var(--pf-surface-2)] rounded" data-testid={`resend-${inv.id}`}>
                        <PaperPlaneRight size={14}/>
                      </button>
                      <button onClick={() => revoke(inv.id)} title="Revoke" className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`revoke-${inv.id}`}>
                        <Trash size={14}/>
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
